import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { Organization } from '../../../../internal-user/customer-management/models/organization';
import { OrganizationService } from '../../../../internal-user/customer-management/services/organization.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ActivatedRoute } from '@angular/router';
import * as constants from '../../../../config/app-constants';
import { ToastPanelType } from '../../../../config/app-constants';
import { AssetService } from '../../../../external-user/inventory/services/asset.service';
import { SystemAlertService } from '../../../../external-user/inventory/services/system-alert.service';
import { OrgAssetService } from '../../../../external-user/inventory/services/org-asset.service';
import { Asset } from '../../../../external-user/inventory/models/asset';
import { AuthService } from '../../../../auth/auth.service';
import { Observable, forkJoin, switchMap, map, tap, catchError, of } from 'rxjs';
import { Role } from '../../../../shared/models/auth/role';
import { noInternalWhitespaceValidator } from 'src/app/shared/validators/whitespace.validator';

@Component({
    selector: 'app-customer-survey',
    templateUrl: './customer-survey.component.html',
    styleUrls: ['./customer-survey.component.scss'],
    standalone: false
})
export class CustomerSurveyComponent implements AfterViewInit {

  @ViewChild('formDirective', { static: true }) private formDirective: NgForm;

  formGroup: FormGroup;
  fb: FormBuilder = new FormBuilder();
  updateFormGroup: FormGroup;
  organization: Organization;
  ltMachineTypes: any[] = [];
  orgId: string;
  orgType: string;
  orgSalesforceId: string;
  selectedMachineTypeText: string;
  assets$: Observable<Asset[]> = of([]);
  isAdmin: boolean = false;
  userCanSubscribe: boolean = false;
  subscriptions = new Set();
  
  constructor(
    private organizationService: OrganizationService,
    private assetService: AssetService,
    private orgAssetService: OrgAssetService,
    private toastService: ToastService,
    private ngxLoader: NgxUiLoaderService,
    private route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly systemAlertService: SystemAlertService
  ) {
    this.updateFormGroup = this.fb.group({
      systems: this.fb.array([])
    });
      
    this.formGroup = new FormGroup({
      FriendlyName: new FormControl('', [Validators.required]),
      SerialNumber: new FormControl('', [Validators.required, noInternalWhitespaceValidator]),
      MachineType: new FormControl('', [Validators.required]),
    });
  }

  ngAfterViewInit(): void {
    this.authService.profile$.subscribe((profile) => {
      this.isAdmin = profile.hasRole(Role.admin) || profile.hasRole(Role.orgAdmin);
      this.userCanSubscribe = profile.hasSystemAlertingAccess();
      if (this.userCanSubscribe) this.initializeSubscriptions();
    });
    
    if (this.route.parent && typeof this.route.parent !== 'undefined') {
      this.route.parent.params.subscribe(({ id }) => {
        this.loadAssetsForOrg(id);
      });
    } else {
      this.authService.profile$.subscribe((profile) => {
        this.orgId = profile.organizationId;
        this.orgSalesforceId = profile.organizationSfId;
        this.orgType = profile.orgType;

        this.loadAssetsForOrg(this.orgId);
      });
    }
  }
    
  get systems(){
    return this.updateFormGroup.controls['systems'] as FormArray;
  }
  loadAssetsForOrg(id) {
    const loadingKey = 'loadAssetsByOrg';
    this.ngxLoader.start(loadingKey);
    this.updateFormGroup.setControl('systems', this.fb.array([]));
    this.assets$ = this.organizationService.getByID(id)
    .pipe(
      switchMap(org => {
        this.organization = org;
        this.orgId = this.organization.organizationId;
        this.orgSalesforceId = this.organization.salesforceId;
        return forkJoin({
          assets:this.assetService.getAssetByOrgId(this.orgId).pipe(catchError(e => {
            console.log("failed to load assets");
            this.toastService.openToast('Failed to load assets!', ToastPanelType.error);
            return of([]);
          })),
          ltMachineTypes:this.assetService.getActiveMachinesType().pipe(catchError(e => {console.log("failed to load machine types"); return of([]);})),
        });
      }),
      tap(val =>{
        if(val.ltMachineTypes){
          this.ltMachineTypes = this.sortMachineTypesByDisplayName(val.ltMachineTypes);
        }
      }),
      map((val) => {
        if(val.assets){
          val.assets.map(asset => {
            if (asset.configurations) {
              asset.configurations.machineType = this.ltMachineTypes.find(machType => machType.machineTypeId == asset.configurations.machineTypeID)?.name ?? "unknown";
            }
            return asset;
          });
          return val.assets.sort(this.sortAssets);
        }
        return [];
      }),
      tap((val : Asset[]) =>{
        if(val){
          val.forEach(asset => {
            const systemForm = this.fb.group({
              friendlyName:[{value: asset.name, disabled: true}, Validators.required],
              serialNo:[{value:asset.machineNo, disabled:true}, Validators.required, noInternalWhitespaceValidator],
              assetId:[asset.assetId],
              machineTypeID:[asset?.configurations?.machineTypeID],
              valid:[true]
            });
            this.systems.push(systemForm);
          });
        }
        this.ngxLoader.stop(loadingKey);
      })
    );
  }

  sortAssets(a:Asset, b: Asset) {  
    if (a.configurations.machineType === b.configurations.machineType) {
      // If machineType is the same, sort by name
      return a.name.localeCompare(b.name);
    }
    // If machineType is different, sort by machineType
    return a.configurations.machineType.localeCompare(b.configurations.machineType);
  }
  
  deleteOrgAsset(asset) {
    const loadingKey = 'deleteAssetById';
    this.ngxLoader.start(loadingKey);
    this.orgAssetService.deleteAssetById(this.orgId, asset?.assetId,asset?.configurations?.machineType)
      .subscribe({
        next: (assets) => {
          this.reset();
          this.toastService.openToast('System deleted!', constants.ToastPanelType.done);
          this.loadAssetsForOrg(this.orgId);
        },
        error: (error) => {
          this.reset();
          // Check if the error is a BadRequest with the specific message
          if (error.status === 400 && error.error === 'System is already configured to an edge device and hence cannot be deleted') {
            this.toastService.stopLoadingAndShowError(error, error.error, loadingKey);
          } else {
            this.toastService.stopLoadingAndShowError(error, 'Error while deleting asset!', loadingKey);
          }
          this.loadAssetsForOrg(this.orgId);
        },
        complete: () => {
          this.ngxLoader.stop(loadingKey);
        }
      });
  }
  SystemEnabled(index){
    return this.systems.at(index).get('friendlyName').enabled;
  }
  resetOrgAsset(asset, index) {
    this.systems.at(index).get('friendlyName').setValue(asset.name);
    this.systems.at(index).get('friendlyName').disable();
  }

  editOrgAsset(index) {
    this.systems.at(index).get('friendlyName').enable();
  }

  updateOrgAsset(asset, index) {
    if(this.systems.at(index).get('friendlyName').invalid){
      return;
    }
      
    if (!this.FriendlyNameUnique(this.systems.at(index).get('friendlyName').value, asset))  {
      return;
    }

    const loadingKey = 'update';
    this.ngxLoader.start(loadingKey);
    
    this.orgAssetService.updateOrgAsset(this.orgId,asset.assetId, {
      name: this.systems.at(index).get('friendlyName').value,
      description: this.systems.at(index).get('friendlyName').value,
    })
    .subscribe({
      next:() => {
        this.toastService.openToast('System Updated!', constants.ToastPanelType.done);
        this.loadAssetsForOrg(this.orgId);
        this.systems.at(index).get('editable').setValue(false);
        this.systems.at(index).get('friendlyName').disable();

      },
      error:(response) => {
        this.toastService.stopLoadingAndShowError(response, 'Error while saving assets!', loadingKey);
      },
      complete:() =>{
        this.ngxLoader.stop(loadingKey);
      }
    });
  }

  getInnerText(innerText) {
    this.selectedMachineTypeText = innerText;
  }

  getMachineTypeDisplayName(machineType: { machineTypeId?: number | string; name?: string }): string {
    return Number(machineType?.machineTypeId) === 6
      ? 'Tempo-ATP'
      : machineType?.name ?? '';
  }

  sortMachineTypesByDisplayName(machineTypes: any[]): any[] {
    return machineTypes
      .map(machineType => ({
        machineType,
        displayName: this.getMachineTypeDisplayName(machineType)
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map(({ machineType }) => machineType);
  }

  getAssetMachineTypeDisplayName(asset: Asset): string {
    return this.getMachineTypeDisplayName({
      machineTypeId: asset?.configurations?.machineTypeID,
      name: asset?.configurations?.machineType
    });
  }

  SerialNoUniqueValidation(value, asset) {
    if(!value){
      return true;
    }

    const enteredSerialLower = value.toLocaleLowerCase();
    const selectedMachineTypeId = this.formGroup.get('MachineType')?.value?.toString();

    // Find any existing system with same serial
    const duplicateSameType = this.systems.controls.some(element => {
      const existingSerial = element.get('serialNo')?.value?.toLocaleLowerCase();
      const existingMachineTypeId = element.get('machineTypeID')?.value?.toString();
      const isSameSerial = !!existingSerial && existingSerial === enteredSerialLower;
      // If editing an existing asset skip comparing to itself
      const isDifferentAsset = (!asset || asset.assetId !== element.get('assetId')?.value);
      // Conflict only if machine types match (or we can't determine selected type yet)
      const machineTypeMatches = selectedMachineTypeId && existingMachineTypeId && selectedMachineTypeId === existingMachineTypeId;
      return isSameSerial && isDifferentAsset && machineTypeMatches;
    });

    if(duplicateSameType){
      this.toastService.openToast('Serial number already exists for this system type!', ToastPanelType.error);
      return false;
    }

    return true;
  }
  
  FriendlyNameUnique(value, asset) {
    if( this.systems.controls.some((element) => 
      (element.get('friendlyName').value &&
      element.get('friendlyName').value.toLocaleLowerCase() == value.toLocaleLowerCase()) &&
      (!asset || asset.assetId !== element.get('assetId').value))){
        this.toastService.openToast('Friendly Name should be Unique!', ToastPanelType.error);
        return false;      
    }

    return true;
  }

  SystemFriendlyNameValid(index){
    return this.systems.at(index).get('friendlyName').hasError('required');
  }

  submit() {

    if (this.formGroup.invalid) {
      this.formGroup.get('FriendlyName').markAsTouched();
      this.formGroup.get('MachineType').markAsTouched();
      this.formGroup.get('SerialNumber').markAsTouched();
      this.toastService.openToast('Please complete the form before submit!', ToastPanelType.error);
      return;
    }

    if (!this.FriendlyNameUnique(this.formGroup.get('FriendlyName').value, null))  {
      return;
    }
    if (!this.SerialNoUniqueValidation(this.formGroup.get('SerialNumber').value, null) )  {
      return;
    }

    const loadingKey = 'save';
    this.orgAssetService.createOrgAsset(this.orgId, {
      assetTypeId: 5,
      name: this.formGroup.get('FriendlyName').value,
      description: this.formGroup.get('FriendlyName').value,
      machineNo: this.formGroup.get('SerialNumber').value,
      configurations: {
        machineTypeID: this.formGroup.get('MachineType').value,
        operatingSystem: this.selectedMachineTypeText
      }
    }).subscribe({
      next:() => {
        this.toastService.openToast('System created!', constants.ToastPanelType.done);
        this.loadAssetsForOrg(this.orgId);
        this.reset();
      },
      error: (response) => { 
        if(response.status == 409)
          this.toastService.stopLoadingAndShowError(response, response.error, loadingKey);  
        else
          this.toastService.stopLoadingAndShowError(response, 'Error while saving assets!', loadingKey);
        this.loadAssetsForOrg(this.orgId); 
      }
    });
    this.ngxLoader.stop(loadingKey);
  }

  reset() {
    this.formDirective.resetForm();
    this.formGroup.reset({
      FriendlyName: '',
      SerialNumber: '',
      MachineType: []
    });
  }

  initializeSubscriptions()
  {
    let subscriptions: Asset[] = [];
    if (!this.userCanSubscribe) return;

    this.systemAlertService.getSubscribedAssets().subscribe({
      next: (response) => { 
        subscriptions = response;
        subscriptions.forEach((asset) => { this.initializeSubscription(asset); });
      },
      error: (response) => { this.toastService.stopLoadingAndShowError(response, response.error); }
    });
  }

  onSubscriptionChange(asset: Asset, event: any)
  {
    const systemId = asset.machineNo;
    const systemType = asset.configurations.machineType;
    const displaySystemType = this.getAssetMachineTypeDisplayName(asset);
    const enable = event.target.checked;
    
    const loadingKey = 'updateSubscription';
    this.ngxLoader.start(loadingKey);
    this.systemAlertService.updateSubscription(systemId, systemType, enable).subscribe({
      next: () => { 
        this.toastService.openToast(`Subscription to ${systemId} (${displaySystemType}) updated`, constants.ToastPanelType.done); 
        if (enable) { this.subscriptions.add(asset.assetId); }
        else { this.subscriptions.delete(asset.assetId); }
        this.ngxLoader.stop(loadingKey);
      },
      error: (response) => { 
        this.toastService.stopLoadingAndShowError(response, response.error, loadingKey); 
        this.ngxLoader.stop(loadingKey);
      }
    });
  }

  initializeSubscription(asset: Asset)
  {
    if (asset == null) return;
    this.subscriptions.add(asset.assetId);
  }
}
